/**
 * Pure bar-position/clipping math for TimelineView — ported from smart-web's
 * IncidentTimeline.tsx, kept independent of React so it's unit-testable.
 */

export const ROW_HEIGHT = 30
export const ROW_STEP = ROW_HEIGHT + 4

export interface TimelineBarInput {
  id: string
  /** Swimlane this bar belongs to. */
  rowKey: string
  startMs: number
  /** null/undefined = open-ended, still running as of `nowMs`. */
  endMs: number | null | undefined
}

export interface TimelineBar {
  id: string
  rowKey: string
  openedMs: number
  /** Resolved end — `nowMs` (clamped to rangeEnd) when still open. */
  closedMs: number
  isOpen: boolean
  /** Left edge as a 0–100 percent of the visible range. */
  leftPct: number
  /**
   * Width as a 0–100 percent of the visible range — the true, unclamped
   * duration. Any "still visible/clickable" minimum belongs at render time as
   * a pixel-based CSS minWidth (TimelineView's MIN_BAR_WIDTH_PX), not baked in
   * here as a percentage: a percentage floor doesn't scale with the range
   * (e.g. 0.35% is ~5min over a 24h view but ~35min over a 7-day one) and,
   * unlike a CSS minWidth, it permanently destroys the real duration for
   * every bar it clamps, making differently-sized short incidents look
   * artificially identical.
   */
  widthPct: number
  /** True when the incident's true start/end lies outside the visible range —
   *  used to square off the bar's clipped edge instead of rounding it. */
  clipsLeft: boolean
  clipsRight: boolean
}

/**
 * Computes bar geometry for every input within [rangeStart, rangeEnd]. Inputs
 * with a NaN start, or a non-positive range, are dropped.
 */
export function computeBars(
  inputs: TimelineBarInput[],
  rangeStart: number,
  rangeEnd: number,
  nowMs: number = Date.now(),
): TimelineBar[] {
  const spanMs = rangeEnd - rangeStart
  if (spanMs <= 0) return []

  const toPct = (ms: number) => ((ms - rangeStart) / spanMs) * 100

  const bars: TimelineBar[] = []
  for (const input of inputs) {
    const openedMs = input.startMs
    if (!Number.isFinite(openedMs)) continue

    const isOpen = input.endMs == null
    const closedMs = isOpen ? Math.min(nowMs, rangeEnd) : input.endMs!

    const clipsLeft = openedMs < rangeStart
    const clipsRight = closedMs > rangeEnd

    const barStartMs = Math.max(openedMs, rangeStart)
    const barEndMs = Math.min(closedMs, rangeEnd)

    const leftPct = Math.min(100, Math.max(0, toPct(barStartMs)))
    const rightPct = Math.min(100, Math.max(0, toPct(barEndMs)))
    const widthPct = rightPct - leftPct

    bars.push({
      id: input.id,
      rowKey: input.rowKey,
      openedMs,
      closedMs,
      isOpen,
      leftPct,
      widthPct,
      clipsLeft,
      clipsRight,
    })
  }

  return bars.sort((a, b) => a.openedMs - b.openedMs)
}

/** Which swimlane row index a pointer y-offset falls in, given a list of row keys in display order. */
export function rowIndexFromY(y: number): number {
  return Math.floor(y / ROW_STEP)
}
